import { useState, useEffect, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScoreBadge, STATES, currency } from "@/components/leadUtils";
import { BulkImportDialog } from "@/components/BulkImportDialog";
import { Users, Inbox, CheckCircle2, Flame, Plus, Trash2, Loader2, Database, MapPin, DollarSign } from "lucide-react";

const StatCard = ({ Icon, label, value, testid }) => (
  <Card data-testid={testid} className="bg-slate-900 border-slate-800/80 rounded-xl p-5 flex items-center gap-4">
    <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
      <Icon className="h-5 w-5 text-emerald-400" />
    </div>
    <div>
      <div className="text-xl font-heading font-bold text-white">{value}</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wider font-mono">{label}</div>
    </div>
  </Card>
);

const TIMELINES = ["0-3 months", "3-6 months", "6-12 months", "12+ months"];
const emptyLead = { name: "", email: "", phone: "", role: "buyer", state: "CA", city: "", zip_code: "", timeline: "0-3 months", budget_min: "", budget_max: "", financing: "pre-approved", notes: "" };

export default function AdminPanel() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLead);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [st, ld, ag, sb] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/agents"),
        api.get("/admin/subscriptions"),
      ]);
      setStats(st.data);
      setLeads(ld.data.leads);
      setAgents(ag.data.agents);
      setSubs(sb.data.subscriptions);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createLead = async () => {
    if (!form.name || !form.email || !form.phone || !form.zip_code) {
      toast.error("Name, email, phone and ZIP are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/leads", {
        ...form,
        budget_min: parseInt(form.budget_min || 0, 10),
        budget_max: parseInt(form.budget_max || form.budget_min || 0, 10),
      });
      toast.success("Lead added to the pool.");
      setOpen(false);
      setForm(emptyLead);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add lead");
    } finally {
      setSaving(false);
    }
  };

  const deleteLead = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted.");
      load();
    } catch (e) {
      toast.error("Failed to delete lead");
    }
  };

  const releaseZip = async (zip) => {
    if (!window.confirm(`Release ZIP ${zip}? The agent will stop receiving its leads.`)) return;
    try {
      await api.delete(`/admin/subscriptions/${zip}`);
      toast.success(`ZIP ${zip} released.`);
      load();
    } catch (e) {
      toast.error("Failed to release ZIP");
    }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      await api.post("/admin/seed-leads");
      toast.success("Sample leads loaded.");
      load();
    } catch (e) {
      toast.error("Failed to seed leads");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Admin Command Center</h1>
            <p className="text-slate-400 mt-1">Manage leads, agents and territory coverage</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <BulkImportDialog onDone={load} />
            <Button onClick={seed} disabled={seeding} variant="outline" className="border-slate-700 bg-transparent text-white hover:bg-slate-800 gap-2" data-testid="admin-seed-btn">
              <Database className="h-4 w-4" /> {seeding ? "Loading…" : "Load Samples"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold gap-2" data-testid="admin-upload-lead-btn">
                  <Plus className="h-4 w-4" /> Add Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">Add New Lead</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-400">Name</Label><Input data-testid="admin-lead-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-slate-950 border-slate-800 mt-1" /></div>
                    <div><Label className="text-slate-400">Phone</Label><Input data-testid="admin-lead-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-slate-950 border-slate-800 mt-1" /></div>
                  </div>
                  <div><Label className="text-slate-400">Email</Label><Input data-testid="admin-lead-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-slate-950 border-slate-800 mt-1" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-400">Type</Label>
                      <Select value={form.role} onValueChange={(v) => set("role", v)}>
                        <SelectTrigger data-testid="admin-lead-role" className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="buyer">Buyer</SelectItem>
                          <SelectItem value="seller">Seller</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-400">State</Label>
                      <Select value={form.state} onValueChange={(v) => set("state", v)}>
                        <SelectTrigger data-testid="admin-lead-state" className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-400">City</Label><Input data-testid="admin-lead-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-slate-950 border-slate-800 mt-1" /></div>
                    <div><Label className="text-slate-400">ZIP</Label><Input data-testid="admin-lead-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} className="bg-slate-950 border-slate-800 mt-1" /></div>
                  </div>
                  <div>
                    <Label className="text-slate-400">Timeline</Label>
                    <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                      <SelectTrigger data-testid="admin-lead-timeline" className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-400">Budget min</Label><Input data-testid="admin-lead-budget-min" type="number" value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} className="bg-slate-950 border-slate-800 mt-1" /></div>
                    <div><Label className="text-slate-400">Budget max</Label><Input data-testid="admin-lead-budget-max" type="number" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} className="bg-slate-950 border-slate-800 mt-1" /></div>
                  </div>
                  <div>
                    <Label className="text-slate-400">Financing</Label>
                    <Select value={form.financing} onValueChange={(v) => set("financing", v)}>
                      <SelectTrigger data-testid="admin-lead-financing" className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pre-approved">Pre-approved</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="needs-financing">Needs financing</SelectItem>
                        <SelectItem value="unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-slate-400">Notes</Label><Textarea data-testid="admin-lead-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-slate-950 border-slate-800 mt-1" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={createLead} disabled={saving} className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold" data-testid="admin-lead-save-btn">
                    {saving ? "Saving…" : "Add Lead"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
          <StatCard Icon={Inbox} label="Total Leads" value={stats?.total ?? "–"} testid="admin-stat-total" />
          <StatCard Icon={CheckCircle2} label="In Owned ZIPs" value={stats?.assigned ?? "–"} testid="admin-stat-assigned" />
          <StatCard Icon={Flame} label="Hot Leads" value={stats?.hot ?? "–"} testid="admin-stat-hot" />
          <StatCard Icon={Users} label="Agents" value={stats?.agents ?? "–"} testid="admin-stat-agents" />
          <StatCard Icon={MapPin} label="ZIPs Sold" value={stats ? `${stats.subscriptions}/${stats.subscriptions + stats.zips_available}` : "–"} testid="admin-stat-zips" />
          <StatCard Icon={DollarSign} label="Monthly Revenue" value={stats ? currency(stats.mrr) : "–"} testid="admin-stat-mrr" />
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>
        ) : (
          <Tabs defaultValue="leads">
            <TabsList className="bg-slate-900 border border-slate-800">
              <TabsTrigger value="leads" data-testid="admin-tab-leads" className="data-[state=active]:bg-emerald-400 data-[state=active]:text-slate-950">Leads ({leads.length})</TabsTrigger>
              <TabsTrigger value="agents" data-testid="admin-tab-agents" className="data-[state=active]:bg-emerald-400 data-[state=active]:text-slate-950">Agents ({agents.length})</TabsTrigger>
              <TabsTrigger value="territories" data-testid="admin-tab-territories" className="data-[state=active]:bg-emerald-400 data-[state=active]:text-slate-950">ZIP Territories ({subs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="territories" className="mt-6">
              <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800 hover:bg-transparent">
                        <TableHead className="text-slate-400">ZIP</TableHead>
                        <TableHead className="text-slate-400">Area</TableHead>
                        <TableHead className="text-slate-400">Population</TableHead>
                        <TableHead className="text-slate-400">Tier</TableHead>
                        <TableHead className="text-slate-400">Price</TableHead>
                        <TableHead className="text-slate-400">Agent</TableHead>
                        <TableHead className="text-slate-400">Leads</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subs.length === 0 ? (
                        <TableRow className="border-slate-800"><TableCell colSpan={8} className="text-center text-slate-500 py-10">No ZIP territories sold yet.</TableCell></TableRow>
                      ) : subs.map((s) => (
                        <TableRow key={s.zip_code} data-testid={`admin-sub-row-${s.zip_code}`} className="border-slate-800 hover:bg-slate-800/40">
                          <TableCell className="font-mono font-semibold text-white">{s.zip_code}</TableCell>
                          <TableCell className="text-slate-400">{s.city}, {s.state}</TableCell>
                          <TableCell className="text-slate-400">{s.population.toLocaleString()}</TableCell>
                          <TableCell className="capitalize text-slate-400">{s.tier}</TableCell>
                          <TableCell className="text-emerald-400 font-semibold">${s.price}/mo</TableCell>
                          <TableCell className="text-slate-300">{s.agent_name}<div className="text-xs text-slate-500">{s.agent_email}</div></TableCell>
                          <TableCell className="text-slate-200">{s.lead_count}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => releaseZip(s.zip_code)} className="text-slate-500 hover:text-rose-400" data-testid={`admin-release-zip-${s.zip_code}`}>Release</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="leads" className="mt-6">
              <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800 hover:bg-transparent">
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">Type</TableHead>
                        <TableHead className="text-slate-400">Location</TableHead>
                        <TableHead className="text-slate-400">Budget</TableHead>
                        <TableHead className="text-slate-400">Score</TableHead>
                        <TableHead className="text-slate-400">Source</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((l) => (
                        <TableRow key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-slate-800 hover:bg-slate-800/40">
                          <TableCell className="font-medium text-white">{l.name}</TableCell>
                          <TableCell className="capitalize text-slate-400">{l.role}</TableCell>
                          <TableCell className="text-slate-400"><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{l.state} · {l.zip_code}</span></TableCell>
                          <TableCell className="text-slate-400">{currency(l.budget_max)}</TableCell>
                          <TableCell><ScoreBadge intent={l.intent} score={l.score} /></TableCell>
                          <TableCell className="text-slate-500 font-mono text-xs uppercase">{l.source}</TableCell>
                          <TableCell>
                            <Button size="icon" variant="ghost" onClick={() => deleteLead(l.id)} className="text-slate-500 hover:text-rose-400" data-testid={`admin-delete-lead-${l.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="agents" className="mt-6">
              <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800 hover:bg-transparent">
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">Email</TableHead>
                        <TableHead className="text-slate-400">Brokerage</TableHead>
                        <TableHead className="text-slate-400">ZIPs Owned</TableHead>
                        <TableHead className="text-slate-400">Monthly</TableHead>
                        <TableHead className="text-slate-400">Leads</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {agents.length === 0 ? (
                        <TableRow className="border-slate-800"><TableCell colSpan={6} className="text-center text-slate-500 py-10">No agents registered yet.</TableCell></TableRow>
                      ) : agents.map((a) => (
                        <TableRow key={a.id} data-testid={`admin-agent-row-${a.id}`} className="border-slate-800 hover:bg-slate-800/40">
                          <TableCell className="font-medium text-white">{a.name}</TableCell>
                          <TableCell className="text-slate-400">{a.email}</TableCell>
                          <TableCell className="text-slate-400">{a.brokerage || "–"}</TableCell>
                          <TableCell className="text-slate-400 font-mono text-xs">{(a.zip_codes || []).join(", ") || "–"}</TableCell>
                          <TableCell className="text-emerald-400 font-semibold">${a.monthly_cost}/mo</TableCell>
                          <TableCell className="text-slate-200 font-semibold">{a.lead_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
